'use client';
import React, { useContext, useState } from 'react';
import { cn } from '@/lib/cn';
import { Popover, PopoverContext } from '@/components/ui/Popover';
import { Button } from '@/components/ui';
import { NeighborhoodMapPopover } from './components/NeighborhoodMapPopover';
import { FilterActionBar } from '../FilterActionBar';
import { useStore } from '@/providers/StoreProvider';
import { LocationFilterContent } from './LocationFilterContent';
import { useAnalytics } from '@/hooks/useAnalytics';

interface LocationFilterProps {
  searchQuery: string;
  onSearchQueryChange?: (query: string) => void;
  className?: string;
  anchorRef?: React.RefObject<HTMLElement>;
  requireConfirmation?: boolean;
  primaryButtonText?: string;
  shouldUpdateUrl?: boolean;
  showSuggestedLocations?: boolean;
  showBuildingSuggestions?: boolean;
  selectionMode?: 'store' | 'external';
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  onResetExternalSelection?: () => void;
  onApplyExternalSelection?: (ids: string[]) => void | Promise<void>;
}

export function LocationFilter({
  searchQuery,
  onSearchQueryChange,
  className,
  anchorRef,
  requireConfirmation,
  primaryButtonText,
  shouldUpdateUrl,
  showSuggestedLocations = true,
  showBuildingSuggestions = true,
  selectionMode = 'store',
  selectedNeighborhoodIds,
  onToggleNeighborhood,
  onToggleNeighborhoodWithChildren,
  onToggleBorough,
  onResetExternalSelection,
  onApplyExternalSelection,
}: LocationFilterProps) {
  const [showMapPopover, setShowMapPopover] = useState(false);
  const resetBuilding = useStore(s => s.listingsFilters.resetBuilding);
  const resetNeighborhoods = useStore(s => s.listingsFilters.resetNeighborhoods);
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const analytics = useAnalytics();
  const popoverContext = useContext(PopoverContext);

  return (
    <div
      className={cn(
        'rounded-3xl bg-primary border border-tertiary shadow-lg',
        'max-h-[500px] h-auto overflow-y-auto min-w-md hide-scrollbar',
        className,
      )}
    >
      <LocationFilterContent
        searchQuery={searchQuery}
        variant="desktop"
        onViewMap={() => setShowMapPopover(true)}
        showSuggestedLocations={showSuggestedLocations}
        showBuildingSuggestions={showBuildingSuggestions}
        selectionMode={selectionMode}
        {...(onSearchQueryChange ? { onSearchQueryChange } : {})}
        {...(selectionMode === 'external'
          ? {
              selectedNeighborhoodIds: selectedNeighborhoodIds ?? [],
              onToggleNeighborhood,
              onToggleNeighborhoodWithChildren,
              onToggleBorough,
            }
          : {})}
      />

      {anchorRef && (
        <Popover
          open={showMapPopover}
          onOpenChange={setShowMapPopover}
          anchorRef={anchorRef}
          matchAnchorWidth={false}
          className="z-50"
        >
          <NeighborhoodMapPopover
            onClose={() => setShowMapPopover(false)}
            selectionMode={selectionMode}
            {...(selectionMode === 'external'
              ? {
                  selectedNeighborhoodIds: selectedNeighborhoodIds ?? [],
                  onToggleNeighborhood,
                  onToggleNeighborhoodWithChildren,
                  onToggleBorough,
                  onResetExternalSelection,
                  onApplyExternalSelection,
                }
              : {})}
          />
        </Popover>
      )}

      {requireConfirmation && searchQuery.length === 0 && selectionMode === 'store' && (
        <div className="sticky bottom-0 bg-primary rounded-b-2xl pb-2">
          <FilterActionBar
            scope="location"
            {...(primaryButtonText ? { primaryButtonText } : {})}
            shouldUpdateUrl={shouldUpdateUrl ?? true}
            onApply={() => {
              analytics.track('listings_filter_neighborhood_seeresults', {
                selectedNeighborhoods: neighborhoodIds,
              });
            }}
            onReset={() => {
              analytics.track('listings_filter_neighborhood_reset');
              resetBuilding();
              resetNeighborhoods();
            }}
          />
        </div>
      )}
      {requireConfirmation && searchQuery.length === 0 && selectionMode === 'external' && (
        <div className="sticky bottom-0 bg-primary rounded-b-2xl pb-2">
          <div className="flex items-center gap-4 md:justify-end justify-center py-4 px-6 border-t border-secondary">
            <Button
              variant="tertiary"
              size="sm"
              className="flex-1 md:flex-none min-w-32"
              disabled={!selectedNeighborhoodIds || selectedNeighborhoodIds.length === 0}
              onClick={() => {
                analytics.track('listings_filter_neighborhood_reset');
                onResetExternalSelection?.();
              }}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex-1 md:flex-none min-w-32"
              onClick={() => {
                analytics.track('listings_filter_neighborhood_seeresults', {
                  selectedNeighborhoods: selectedNeighborhoodIds ?? [],
                });
                popoverContext?.close();
              }}
            >
              {primaryButtonText ?? 'Done'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocationFilter;
